import React, { useEffect, useState } from 'react';
import { App, Spin } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { getPrizeClaim, updatePrizeClaimStatus, createPrizeClaim, uploadPaymentReceipt } from '../../services/competitions';
import './ClaimDetailView.css';

// Components
import ClaimHeader from './components/ClaimHeader';
import ClaimPersonalInfo from './components/ClaimPersonalInfo';
import ClaimIdCard from './components/ClaimIdCard';
import ClaimActionButtons from './components/ClaimActionButtons';

// Modals
import RejectClaimModal from './components/modals/RejectClaimModal';
import PaymentConfirmModal from './components/modals/PaymentConfirmModal';

const ClaimDetailView = () => {
  const { message } = App.useApp();
  const { id } = useParams(); // format: winner-compID-videoID
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [claimData, setClaimData] = useState(null);
  const [observations, setObservations] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  useEffect(() => {
    if (id) {
      const parts = id.split('-');
      if (parts.length >= 3) {
        const videoId = parts[2];
        fetchData(videoId);
      } else {
        setLoading(false);
      }
    }
  }, [id]);

  const fetchData = async (videoId) => {
    try {
      setLoading(true);
      let res = await getPrizeClaim(videoId);
      if (res && res.success !== undefined && res.data === null) {
        res = null;
      } else if (res && res.success !== undefined && res.data) {
        res = res.data;
      }
      setClaimData(res);
    } catch (error) {
      console.error(error);
      message.error('Error al cargar la información del ganador.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/prize-claims/winners', { state: { filters: location.state?.filters } });
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!claimData?.id) return;
    try {
      setSubmitting(true);
      await updatePrizeClaimStatus(claimData.id, {
        status: newStatus,
        rejectReason: observations || undefined
      });
      message.success(`Estado actualizado a: ${newStatus}`);
      const parts = id.split('-');
      fetchData(parts[2]);
      setObservations('');
    } catch (error) {
      console.error(error);
      message.error('Error al actualizar el estado.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePaymentConfirm = async (file) => {
    if (!claimData?.id) return;
    try {
      setSubmitting(true);
      let receiptUrl = undefined;
      if (file) {
        const uploadRes = await uploadPaymentReceipt(file);
        receiptUrl = uploadRes?.url || uploadRes?.previewUrl;
      }
      await updatePrizeClaimStatus(claimData.id, {
        status: 'Pagado',
        rejectReason: observations || undefined,
        receiptUrl,
      });
      message.success('Pago registrado exitosamente.');
      setIsPaymentModalOpen(false);
      const parts = id.split('-');
      fetchData(parts[2]);
      setObservations('');
    } catch (error) {
      console.error(error);
      message.error('Error al registrar el pago.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRejectSubmit = async (rejectReason, rejectNotes) => {
    if (!claimData?.id || !rejectReason.trim()) return;
    try {
      setSubmitting(true);
      await updatePrizeClaimStatus(claimData.id, {
        status: 'Corrección Solicitada',
        rejectReason: rejectNotes ? `${rejectReason}\n\nObservaciones: ${rejectNotes}` : rejectReason,
      });
      message.success('Solicitud de corrección enviada al participante.');
      const parts = id.split('-');
      fetchData(parts[2]);
      setIsRejectModalOpen(false);
    } catch (error) {
      console.error(error);
      message.error('Error al solicitar la corrección.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSimulateSubmitForm = async () => {
    if (!id) return;
    try {
      setSubmitting(true);
      const parts = id.split('-');
      const videoId = parseInt(parts[2], 10);
      const clientId = data.clientId || 1;
      
      const payload = {
        competitionVideoId: videoId,
        clientId: clientId,
        firstName: data.firstName && data.firstName !== 'Pendiente' ? data.firstName : 'Juan',
        lastName: data.lastName && data.lastName !== 'Pendiente' ? data.lastName : 'Pérez',
        email: data.email && data.email !== 'Pendiente' ? data.email : 'juan@test.com',
        phone: data.phone && data.phone !== 'Pendiente' ? data.phone : '5555555555',
        clabe: '012345678901234567',
        bankName: 'Banamex',
        accountHolder: 'Juan Pérez',
        idImage: 'https://placehold.co/600x400?text=Identificación+Simulada',
      };
      
      await createPrizeClaim(payload);
      message.success('Formulario simulado enviado correctamente desde móvil.');
      fetchData(videoId);
    } catch (error) {
      console.error(error);
      message.error('Error al simular el envío del formulario.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="claim-detail-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const data = claimData || {
    competitionVideo: { competition: { name: 'Desconocido' } },
    status: 'Ganador Calculado',
    createdAt: null,
    updatedAt: null,
    firstName: 'Pendiente',
    lastName: 'Pendiente',
    phone: 'Pendiente',
    email: 'Pendiente',
    bankName: 'Pendiente',
    clabe: 'Pendiente',
    accountHolder: 'Pendiente',
    idImage: 'Pendiente'
  };

  const pendingStatuses = ['Ganador Calculado', 'Formulario Enviado', 'Corrección Solicitada'];
  const isPending = data.firstName === 'Pendiente' || pendingStatuses.includes(data.status);

  return (
    <div className="claim-detail-container">
      
      <ClaimHeader data={data} onBack={handleBack} />

      <div className="claim-content-grid">
        <ClaimPersonalInfo data={data} />
        <ClaimIdCard data={data} isPending={isPending} />
      </div>

      <ClaimActionButtons 
        data={data}
        isPending={isPending}
        submitting={submitting}
        observations={observations}
        setObservations={setObservations}
        onSimulate={handleSimulateSubmitForm}
        onApprove={() => handleUpdateStatus('Aprobado para Pago')}
        onRejectClick={() => setIsRejectModalOpen(true)}
        onPaymentClick={() => setIsPaymentModalOpen(true)}
      />

      <RejectClaimModal 
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        onSubmit={handleRejectSubmit}
        submitting={submitting}
      />

      <PaymentConfirmModal 
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        onConfirm={handlePaymentConfirm}
        submitting={submitting}
      />

      <div className="claim-footer-info">
        <div className="claim-footer-item">
          Asignado a:
          <div className="claim-avatar">AD</div>
          <span className="claim-footer-value">Administrador</span>
        </div>
        
        <div className="claim-footer-item">
          <CalendarOutlined /> Última actualización de estado:
          <span className="claim-footer-value">{data.updatedAt ? dayjs(data.updatedAt).format('DD/MM/YYYY hh:mm a') : '--/--/---- --:--'}</span>
        </div>
      </div>

    </div>
  );
};

export default ClaimDetailView;
